import { PropsWithChildren } from 'react';
import Dialog from '@mui/material/Dialog';
import Drawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { Button } from '@vibTheme/components/_base/Button';
import { Typography } from '@vibTheme/components/_base/Typography';
import { EligibleReferralInvite } from '@interfaces/referralInvite';

export type ReferralInviteStep = 'offer' | 'copied' | 'shared';

type Props = {
  invite: EligibleReferralInvite;
  step: ReferralInviteStep;
  link: string;
  isOpen: boolean;
  onCopy: () => void;
  onShare: () => void;
  onDismiss: () => void;
  onClose: () => void;
};

const formatReais = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(value);

const formatRate = (rate: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(rate);

/** `2026-08` → `agosto`. */
const formatMonth = (revenueMonth: string) => {
  const [year, month] = revenueMonth.split('-').map(Number);

  return new Intl.DateTimeFormat('pt-BR', {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, 15)));
};

const displayLink = (link: string) => link.replace(/^https?:\/\//, '');

export const ReferralInviteDialog = (props: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const content =
    props.step === 'offer' ? (
      <OfferStep {...props} />
    ) : (
      <ConfirmationStep {...props} isMobile={isMobile} />
    );

  if (isMobile) {
    return (
      <Drawer
        anchor="bottom"
        open={props.isOpen}
        onClose={props.onClose}
        PaperProps={{ className: 'rounded-t-2xl max-h-[90vh]' }}
      >
        <div className="flex justify-center pt-sm">
          <span className="block w-10 h-1 rounded-full bg-grayDivider" />
        </div>
        {content}
      </Drawer>
    );
  }

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      aria-labelledby="referral-invite-title"
      PaperProps={{ className: 'rounded-xl max-w-[520px]' }}
    >
      {content}
    </Dialog>
  );
};

function OfferStep(props: Props) {
  const { invite } = props;
  const rate = formatRate(invite.referralRate);

  return (
    <>
      <header className="flex justify-between items-center px-main py-md border-0 border-b border-solid border-grayDivider">
        <Typography className="text-primary text-sm font-semibold uppercase">
          Indicação VibX
        </Typography>
        <CloseButton onClick={props.onClose} />
      </header>

      <div className="grid gap-main p-main">
        <Typography
          id="referral-invite-title"
          component="h2"
          className="text-2xl font-semibold"
        >
          Uma criadora como você te pagaria{' '}
          {formatReais(invite.monthlyProjection)} por mês
        </Typography>

        <Typography className="text-black/60">
          Você faturou{' '}
          <strong className="text-black">
            {formatReais(invite.lastMonthRevenue)}
          </strong>{' '}
          em {formatMonth(invite.revenueMonth)}. Quem você trouxer para a VibX
          te paga <strong className="text-black">{rate}</strong> de tudo que ela
          faturar, <strong className="text-black">todo mês</strong>, enquanto
          ela estiver aqui.
        </Typography>

        <div className="flex justify-between items-center gap-md p-main rounded-xl bg-green-100">
          <Typography className="text-green-800 text-sm">
            Três criadoras faturando como você
          </Typography>
          <div className="text-right shrink-0">
            <Typography className="text-green-800 text-3xl font-bold leading-none">
              {formatReais(invite.threeReferralsProjection)}
            </Typography>
            <Typography className="text-green-800 text-xs font-semibold uppercase">
              por mês
            </Typography>
          </div>
        </div>

        <LinkBox link={props.link}>
          <Button
            variant="text"
            size="medium"
            onClick={props.onCopy}
            className="shrink-0 rounded-md font-semibold"
          >
            Copiar
          </Button>
        </LinkBox>
      </div>

      <footer className="flex flex-col-reverse gap-sm px-main py-md border-0 border-t border-solid border-grayDivider sm:flex-row sm:justify-end sm:gap-md">
        <Button
          variant="outlined"
          onClick={props.onDismiss}
          className="rounded-md"
        >
          Agora não
        </Button>
        <Button onClick={props.onShare} className="rounded-md">
          Compartilhar meu link
        </Button>
      </footer>
    </>
  );
}

function ConfirmationStep(props: Props & { isMobile: boolean }) {
  const rate = formatRate(props.invite.referralRate);

  return (
    <div className="relative grid gap-md justify-items-center px-main pt-main pb-xl text-center">
      {!props.isMobile && (
        <div className="absolute top-md right-md">
          <CloseButton onClick={props.onClose} />
        </div>
      )}

      <span className="flex items-center justify-center w-16 h-16 rounded-full bg-green-50">
        <CheckIcon className="text-green-700 text-3xl" />
      </span>

      <Typography
        id="referral-invite-title"
        component="h2"
        className="text-2xl font-semibold"
      >
        {props.step === 'shared' ? 'Link compartilhado' : 'Link copiado'}
      </Typography>

      <Typography className="text-black/60 max-w-md">
        {props.step === 'copied' && 'Mande para quem já vende conteúdo. '}A
        conta é dela, o trabalho é dela — e{' '}
        <strong className="text-black">{rate}</strong> de tudo que ela faturar
        cai pra você, <strong className="text-black">todo mês</strong>.
      </Typography>

      <LinkBox link={props.link} centered />
    </div>
  );
}

function LinkBox(
  props: PropsWithChildren<{ link: string; centered?: boolean }>,
) {
  return (
    <div
      className={`flex items-center gap-md w-full box-border px-main py-md rounded-xl border border-solid border-grayDivider bg-slate-50 ${
        props.centered ? 'justify-center' : 'justify-between'
      }`}
    >
      <code className="break-all text-sm text-black/80">
        {displayLink(props.link)}
      </code>
      {props.children}
    </div>
  );
}

function CloseButton(props: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label="Fechar"
      className="flex bg-transparent border-0 cursor-pointer p-0"
    >
      <CloseIcon className="text-black/60" />
    </button>
  );
}
